import { type SyntheticEvent, useState, useEffect } from 'react';
import useDebouncedCallback from '@atlassian/jira-platform-use-debounce/src/utils/use-debounce-callback/index.tsx';
import type { Page, Priority, PriorityMappings } from '../../common/ui/types';
import { parsePriorityOnServerToPriority } from '../../common/utils';
import { getPrioritySchemeMappingsRequest } from '../../services/priority-scheme-mappings';
import type { PriorityOnServerPageResponse } from '../../services/types';

export type PriorityMappingControllerParams = {
	/**
	 * SchemeId of the current scheme we are mapping. If null,
	 * we are mapping for a scheme that is being created.
	 *
	 * if schemeId is undefined, we don't make API call because
	 * we are searching for the default schemeId.
	 *
	 * MUST BE MEMORISED
	 */
	schemeId: string | null | undefined;
	/**
	 * If there are no changes these arrays will be empty.
	 *
	 * MUST BE MEMORISED
	 */
	prioritiesDiff: {
		add: string[];
		remove: string[];
	};
	/**
	 * If there are no changes these arrays will be empty.
	 *
	 * MUST BE MEMORISED
	 */
	projectsDiff: {
		add: string[];
		remove: string[];
	};
};

export type PriorityMappingControllerReturn = {
	isError: boolean;
	setIsError: (error: boolean) => void;
	/**
	 * This is checking that the request for getting suggested mappings
	 * is being called.
	 *
	 * If true, the form-view modal onSubmit button will have a spinner, to
	 * indicate we are checking if new changes need to be mapped
	 *
	 * This can also be true when users are fetching different pages when there
	 * are multiple pages of mappings.
	 */
	isLoading: boolean;
	/**
	 * A record of priority mappings for the current scheme.
	 */
	mappings: PriorityMappings;
	/**
	 * Updates the mappings record
	 * @param currentPriorityId
	 * @param newPriority
	 */
	onUpdateMapping: (currentPriorityId: string, newPriority: Priority) => void;
	/**
	 * Given a priority Id, remove all mappings key value pairs where the key is
	 * equal to the removedPriorityId.
	 *
	 * NOTE: that this function is currently useless, as it is called when the user
	 * removes a priority from the priority list. However, our new design will remove
	 * all key value pairs when we fetch suggested mappings. However, we are keeping
	 * this function for when we refactor this controller to imperatively fetch mappings
	 * instead of relying on hooks.
	 *
	 * @param removedPriorityId the priority that was removed from mapping list
	 */
	onRemovePriorityMapping: (removedPriorityId: string) => void;
	/**
	 * Represent the current paginated page of priorities that need to be mapped.
	 */
	page: Page<Priority>;
	/**
	 * Callback to handle modal pagination
	 * @param event
	 * @param page
	 */
	onFetch: (event: SyntheticEvent<Element, Event>, pageNumber: number) => void;
};

const DEFAULT_MAPPING_MODAL_ROW_COUNT = 8;

export const usePriorityMappingsController = ({
	schemeId,
	prioritiesDiff,
	projectsDiff,
}: PriorityMappingControllerParams): PriorityMappingControllerReturn => {
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [page, setPage] = useState<Page<Priority>>({
		currentPage: 1,
		items: [],
		totalItems: 0,
		pageSize: 0,
	});
	const [mappings, setMappings] = useState<PriorityMappings>({});
	const [isError, setIsError] = useState<boolean>(false);

	/**
	 * @param pageNumber one indexed
	 */
	const getSuggestedMappings = async (pageNumber: number) => {
		try {
			setIsLoading(true);
			if (!isError) setIsError(false);
			const response: PriorityOnServerPageResponse = await getPrioritySchemeMappingsRequest({
				...(schemeId && { schemeId: Number(schemeId) }),
				startAt: (pageNumber - 1) * DEFAULT_MAPPING_MODAL_ROW_COUNT,
				maxResults: DEFAULT_MAPPING_MODAL_ROW_COUNT,
				priorities: {
					add: prioritiesDiff.add.map(Number),
					remove: prioritiesDiff.remove.map(Number),
				},
				projects: {
					add: projectsDiff.add.map(Number),
				},
			});
			setPage({
				currentPage: pageNumber,
				items: response.values.map(parsePriorityOnServerToPriority),
				totalItems: response.total,
				pageSize: response.maxResults,
			});
		} catch (error: unknown) {
			setIsError(true);
		} finally {
			setIsLoading(false);
		}
	};

	const checkSuggestedMappings = async () => {
		await getSuggestedMappings(1);
		// Clear all mappings whenever we are checking for mappings
		// i.e. whenever there are any changes to the schemeId, priorities or projects
		// within the scheme
		setMappings({});
	};

	const [debouncedFetchData, cancelDebouncedFetchData] = useDebouncedCallback(
		checkSuggestedMappings,
		300,
	);

	/**
	 * NOTE: schemeId, projectsDiff, prioritiesDiff must be memorised in order to prevent infinite looping.
	 *
	 * Possible alternative solutions in the future given more time:
	 * 1. Imperatively call the fetch and update our page, whenever schemeId, projectsDiff, prioritiesDiff changes
	 *    in the other controllers. onFetch might become annoying to deal with, you
	 * 2. Use Redux or another imperative state management framework to deal with this state -> and have it only
	 *    re-render when the schemeId, projectDiff, prioritiesDiff changes
	 */
	useEffect(() => {
		if (schemeId === undefined) return;
		debouncedFetchData();

		return () => {
			cancelDebouncedFetchData();
		};
	}, [schemeId, projectsDiff, prioritiesDiff, debouncedFetchData, cancelDebouncedFetchData]);

	const onUpdateMapping = (currentPriorityId: string, newPriority: Priority) => {
		setMappings({
			...mappings,
			[currentPriorityId]: newPriority,
		});
	};

	const onRemovePriorityMapping = (removedPriorityId: string) => {
		const newMappings: PriorityMappings = {};
		Object.entries(mappings).forEach(([priorityId, priority]) => {
			if (priority.id !== removedPriorityId) {
				newMappings[priorityId] = priority;
			}
		});
		setMappings(newMappings);
	};

	const onFetch = async (_event: SyntheticEvent<Element, Event>, pageNumber: number) => {
		await getSuggestedMappings(pageNumber);
	};

	return {
		isError,
		setIsError,
		isLoading,
		mappings,
		onUpdateMapping,
		onRemovePriorityMapping,
		page,
		onFetch,
	};
};
